import React, { useState, useEffect } from "react";
import Select from "react-select";
import { message, Spin } from "antd";
import {
  useGetEmailSettingsQuery,
  useSaveEmailSettingsMutation,
  useTestEmailMutation,
} from "../../../../services/settings.service";
import {
  phpMailSettingsSchema,
  smtpMailSettingsSchema,
  zeptoMailSettingsSchema,
  testEmailSchema
} from "../../../../utils/validationSchemas";
import { useFormValidation } from "../../../../hooks/useFormValidation";

const EmailSettings = () => {
  const [mailOption, setMailOption] = useState("php");
  const [formData, setFormData] = useState({
    // PHP Mail
    phpFromAddress: "",
    phpFromName: "",
    // ZeptoMail (API)
    zeptoApiUrl: "https://api.zeptomail.com/v1.1",
    zeptoApiToken: "",
    zeptoFromAddress: "",
    zeptoFromName: "",
    // SMTP
    smtpHost: "",
    smtpUser: "",
    smtpPassword: "",
    smtpPort: "",
    smtpSecurity: "none",
    smtpAuthDomain: "",
  });
  const [testEmail, setTestEmail] = useState("");

  // Validation Hooks - Use the schema based on current mail option
  const phpValidation = useFormValidation(phpMailSettingsSchema);
  const smtpValidation = useFormValidation(smtpMailSettingsSchema);
  const zeptoValidation = useFormValidation(zeptoMailSettingsSchema);
  const testEmailValidation = useFormValidation(testEmailSchema);

  // Get the appropriate validation based on mail option
  const { errors, validateForm, validateField, touchField, hasFieldError, getFieldError } =
    mailOption === "php"
      ? phpValidation
      : mailOption === "zeptomail"
        ? zeptoValidation
        : smtpValidation;

  // API Hooks
  const { data: emailSettingsData, isLoading } = useGetEmailSettingsQuery();
  const [saveSettings, { isLoading: isSaving }] = useSaveEmailSettingsMutation();
  const [sendTestEmail, { isLoading: isTesting }] = useTestEmailMutation();

  // Load initial data
  useEffect(() => {
    if (emailSettingsData?.data) {
      const data = emailSettingsData.data;
      setMailOption(data.mailOption || "php");
      setFormData({
        phpFromAddress: data.phpFromAddress || "",
        phpFromName: data.phpFromName || "",
        zeptoApiUrl: data.zeptoApiUrl || "https://api.zeptomail.com/v1.1",
        zeptoApiToken: data.zeptoApiToken || "",
        zeptoFromAddress: data.zeptoFromAddress || "",
        zeptoFromName: data.zeptoFromName || "",
        smtpHost: data.smtpHost || "",
        smtpUser: data.smtpUser || "",
        smtpPassword: data.smtpPassword || "",
        smtpPort: data.smtpPort || "",
        smtpSecurity: data.smtpSecurity || "none",
        smtpAuthDomain: data.smtpAuthDomain || "",
      });
    }
  }, [emailSettingsData]);

  const selectSecurityOptions = [
    { label: "None", value: "none" },
    { label: "SSL", value: "ssl" },
    { label: "TLS", value: "tls" },
  ];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#008a42" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#008a42",
      },
    }),
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);

    // Validate field on change
    validateField(name, value, newFormData);
  };

  // Handle blur event
  const handleBlur = (fieldName) => {
    touchField(fieldName);
    validateField(fieldName, formData[fieldName], formData);
  };

  // Handle security select change
  const handleSecurityChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      smtpSecurity: selectedOption?.value || "none"
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate entire form
    const { isValid } = await validateForm(formData);

    if (!isValid) {
      // Validation errors will be displayed inline
      return;
    }

    try {
      // If validation passes, save settings
      await saveSettings({
        ...formData,
        mailOption
      }).unwrap();
      message.success('Email settings saved successfully');
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to save email settings');
    }
  };

  // Handle test email
  const handleTestEmail = async () => {
    // Validate test email
    const testEmailResult = await testEmailValidation.validateForm({ email: testEmail });

    if (!testEmailResult.isValid) {
      message.error(testEmailResult.errors.email || 'Please enter a valid email address');
      return;
    }

    // Validate configuration before sending test
    const { isValid } = await validateForm(formData);

    if (!isValid) {
      message.error('Please fix the email configuration errors before sending a test');
      return;
    }

    try {
      // Send test email
      await sendTestEmail({
        email: testEmail,
        mailOption,
        ...formData
      }).unwrap();
      message.success(`Test email sent successfully to ${testEmail}`);
      setTestEmail("");
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to send test email');
    }
  };

  const selectedSecurity = selectSecurityOptions.find(opt => opt.value === formData.smtpSecurity);

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        {/* Page Content */}
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-8 offset-md-2">
              <form onSubmit={handleSubmit}>
                <div className="input-block mb-3">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="mailoption"
                      id="phpmail"
                      value="php"
                      checked={mailOption === "php"}
                      onChange={(e) => setMailOption(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="phpmail">
                      PHP Mail
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="mailoption"
                      id="smtpmail"
                      value="smtp"
                      checked={mailOption === "smtp"}
                      onChange={(e) => setMailOption(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="smtpmail">
                      SMTP
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="mailoption"
                      id="zeptomail"
                      value="zeptomail"
                      checked={mailOption === "zeptomail"}
                      onChange={(e) => setMailOption(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="zeptomail">
                      ZeptoMail (API)
                    </label>
                  </div>
                </div>

                {/* ZeptoMail (API) Settings */}
                <h4 className="page-title">ZeptoMail Settings</h4>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Sender Address {mailOption === "zeptomail" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('zeptoFromAddress') ? 'is-invalid' : ''}`}
                        type="email"
                        name="zeptoFromAddress"
                        placeholder="info@yourdomain.com (verified in ZeptoMail)"
                        value={formData.zeptoFromAddress}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('zeptoFromAddress')}
                        disabled={mailOption !== "zeptomail"}
                      />
                      {getFieldError('zeptoFromAddress') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('zeptoFromAddress')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Sender Name</label>
                      <input
                        className="form-control"
                        type="text"
                        name="zeptoFromName"
                        placeholder="HR - Prince Ebeano Supermarket"
                        value={formData.zeptoFromName}
                        onChange={handleInputChange}
                        disabled={mailOption !== "zeptomail"}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Send Mail Token {mailOption === "zeptomail" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('zeptoApiToken') ? 'is-invalid' : ''}`}
                        type="password"
                        name="zeptoApiToken"
                        placeholder="Zoho-enczapikey ..."
                        value={formData.zeptoApiToken}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('zeptoApiToken')}
                        disabled={mailOption !== "zeptomail"}
                      />
                      {getFieldError('zeptoApiToken') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('zeptoApiToken')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">API URL</label>
                      <input
                        className="form-control"
                        type="text"
                        name="zeptoApiUrl"
                        placeholder="https://api.zeptomail.com/v1.1"
                        value={formData.zeptoApiUrl}
                        onChange={handleInputChange}
                        disabled={mailOption !== "zeptomail"}
                      />
                      <small className="text-muted">Use https://api.zeptomail.eu/v1.1 for the EU data centre.</small>
                    </div>
                  </div>
                </div>
                <h4 className="page-title">PHP Email Settings</h4>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Email From Address {mailOption === "php" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('phpFromAddress') ? 'is-invalid' : ''}`}
                        type="email"
                        name="phpFromAddress"
                        value={formData.phpFromAddress}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('phpFromAddress')}
                        disabled={mailOption !== "php"}
                      />
                      {getFieldError('phpFromAddress') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('phpFromAddress')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Emails From Name {mailOption === "php" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('phpFromName') ? 'is-invalid' : ''}`}
                        type="text"
                        name="phpFromName"
                        value={formData.phpFromName}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('phpFromName')}
                        disabled={mailOption !== "php"}
                      />
                      {getFieldError('phpFromName') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('phpFromName')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <h4 className="page-title m-t-30">SMTP Email Settings</h4>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        SMTP HOST {mailOption === "smtp" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('smtpHost') ? 'is-invalid' : ''}`}
                        type="text"
                        name="smtpHost"
                        value={formData.smtpHost}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('smtpHost')}
                        disabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpHost') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpHost')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        SMTP USER {mailOption === "smtp" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('smtpUser') ? 'is-invalid' : ''}`}
                        type="text"
                        name="smtpUser"
                        value={formData.smtpUser}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('smtpUser')}
                        disabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpUser') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpUser')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        SMTP PASSWORD {mailOption === "smtp" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('smtpPassword') ? 'is-invalid' : ''}`}
                        type="password"
                        name="smtpPassword"
                        value={formData.smtpPassword}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('smtpPassword')}
                        disabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpPassword') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpPassword')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        SMTP PORT {mailOption === "smtp" && <span className="text-danger">*</span>}
                      </label>
                      <input
                        className={`form-control ${hasFieldError('smtpPort') ? 'is-invalid' : ''}`}
                        type="text"
                        name="smtpPort"
                        value={formData.smtpPort}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('smtpPort')}
                        disabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpPort') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpPort')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">SMTP Security</label>
                      <Select
                        options={selectSecurityOptions}
                        value={selectedSecurity}
                        onChange={handleSecurityChange}
                        placeholder="None"
                        styles={customStyles}
                        isDisabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpSecurity') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpSecurity')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        SMTP Authentication Domain
                      </label>
                      <input
                        className={`form-control ${hasFieldError('smtpAuthDomain') ? 'is-invalid' : ''}`}
                        type="text"
                        name="smtpAuthDomain"
                        value={formData.smtpAuthDomain}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('smtpAuthDomain')}
                        disabled={mailOption !== "smtp"}
                      />
                      {getFieldError('smtpAuthDomain') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('smtpAuthDomain')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Test Email Section */}
                <h4 className="page-title m-t-30">Test Email Configuration</h4>
                <div className="row">
                  <div className="col-sm-8">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Test Email Address</label>
                      <input
                        className="form-control"
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="Enter email address to test"
                      />
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-secondary w-100"
                        onClick={handleTestEmail}
                        disabled={isTesting || !testEmail}
                      >
                        {isTesting ? 'Sending...' : 'Send Test Email'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="submit-section">
                  <button
                    className="btn btn-primary submit-btn"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save & Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Page Content */}
      </div>
    </>
  );
};

export default EmailSettings;
